/**
 * Resolving which itinerary items a room booking should check out — the step between a "Book now" tap
 * and the hotel checkout route.
 *
 * Hotel checkout prices ITINERARY ITEMS (`PrepareHotelCheckout(tripId, itemIds)`), so a book button on a
 * rate card has to hand it item ids: reuse any already-planned rooms at that rate, create whatever's
 * missing, and pass the result. A route reached with no ids dead-ends on "Nothing to check out".
 *
 * Shared because three surfaces make exactly this decision — web's `BookRoomButton`, mobile's rate
 * picker, and mobile's `RoomCard` — and the reuse gate below is subtle enough that a second copy would
 * drift from the server's own checkout gate.
 */

import type { ItineraryItem } from '#models/ItineraryItemModel.js';

/** The subset of `ItineraryState` this needs — passed in so the helper stays free of the store. */
export interface RoomCheckoutItinerary {
  getAllItineraryItemsByReference(
    type: string,
    referenceId: string,
    metadata?: Record<string, unknown>,
  ): ItineraryItem[];
  addItemToItinerary(input: {
    type: 'room';
    title: string;
    referenceId?: string;
    metadata?: Record<string, unknown>;
    quantity?: number;
  }): Promise<ItineraryItem[]>;
}

export interface ResolveRoomCheckoutItemsArgs {
  itineraryState: RoomCheckoutItinerary;
  /** The room option being booked. */
  roomId: string;
  /** Title for any item this creates (e.g. "Deluxe King Room at The Ritz"). */
  title: string;
  /** The rate identity persisted on the item — `{ productCode, occupancy }`. */
  metadata: Record<string, unknown>;
  /**
   * The identity-narrowed metadata used to LOOK UP existing items (`metadataForIdentity('room', …)`).
   * Pass the same value the rate card's other actions use, so reuse matches the rate on screen.
   */
  identityMeta: Record<string, unknown> | undefined;
  /** How many rooms of this rate to book. */
  count: number;
}

/**
 * True when an item can still be sent to a fresh checkout: it is only planned. The server's checkout gate
 * rejects every other status, and a single poisoned id fails the whole all-or-nothing checkout, so this
 * mirrors that gate. A planned room keeping the `bookingGroupId` of a checkout that never booked it is
 * still an added room, and can be booked again.
 */
export function isReusableForCheckout(item: ItineraryItem): boolean {
  return item.isPlanned;
}

/**
 * The itinerary item ids to check out for `count` rooms of one rate: reuses re-bookable items already on
 * the itinerary, then creates the shortfall. Returns `[]` when creation failed (the store has already
 * surfaced a toast), which callers must treat as "don't navigate".
 */
export async function resolveRoomCheckoutItems({
  itineraryState,
  roomId,
  title,
  metadata,
  identityMeta,
  count,
}: ResolveRoomCheckoutItemsArgs): Promise<string[]> {
  const existing = itineraryState.getAllItineraryItemsByReference('room', roomId, identityMeta);
  const itemIds = existing
    .filter(isReusableForCheckout)
    .slice(0, count)
    .map((item) => item.id);

  const missing = count - itemIds.length;
  if (missing <= 0) return itemIds;

  const added = await itineraryState.addItemToItinerary({
    type: 'room',
    title,
    referenceId: roomId,
    metadata,
    ...(missing > 1 ? { quantity: missing } : {}),
  });
  // Nothing was created (the store toasted the failure) — a partial set would check out fewer rooms than
  // the guest asked for, so treat it as a no-go rather than proceeding with what we have.
  if (added.length === 0) return [];

  return [...itemIds, ...added.map((item) => item.id)];
}
